/* eslint-disable */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
    Table, Button, Tag, Space, Row, Col, Tooltip, Avatar, DatePicker, Input,
    Select, Drawer, Descriptions, Divider, Empty, Spin, message, Modal,
} from 'antd';
import {
    FilePdfOutlined, FileExcelOutlined,
    PrinterOutlined, ShareAltOutlined, CheckCircleOutlined,
    CloseCircleOutlined, ClockCircleOutlined, SwapOutlined,
    UserOutlined, MailOutlined, PhoneOutlined, IdcardOutlined,
    DollarCircleOutlined, SearchOutlined, FilterOutlined, ReloadOutlined,
    EyeOutlined, CopyOutlined, ArrowLeftOutlined,
} from '@ant-design/icons';
import PageBreadcrumb from '../../../components/PageBreadcrumb';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import { customBaseUrl, httpGet } from '../../../store/http';
import { showLoader, hideLoader } from '../../../utils/loader';
import './ProductTransactions.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

/* ──────────────────────────── Status Tag ───────────────────────────── */
const StatusTag = ({ status }) => {
    const s = (status || '').toUpperCase();
    const map = {
        SUCCESS: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
        SUCCESSFUL: { color: '#52c41a', bg: '#f6ffed', border: '#b7eb8f', icon: <CheckCircleOutlined /> },
        FAILED: { color: '#ff4d4f', bg: '#fff2f0', border: '#ffa39e', icon: <CloseCircleOutlined /> },
        PENDING: { color: '#faad14', bg: '#fffbe6', border: '#ffe58f', icon: <ClockCircleOutlined /> },
        REVERSED: { color: '#ff6700', bg: '#fff3eb', border: '#ffbb96', icon: <SwapOutlined /> },
    };
    const c = map[s] || { color: '#595959', bg: '#f5f5f5', border: '#d9d9d9', icon: null };
    return (
        <Tag
            icon={c.icon}
            style={{
                color: c.color, background: c.bg, border: `1px solid ${c.border}`,
                borderRadius: 20, fontWeight: 600, fontSize: 11, padding: '2px 10px',
            }}
        >
            {s}
        </Tag>
    );
};

/* ──────────────────────────── Amount Formatter ───────────────────────── */
const formatAmount = (amt) => {
    if (amt === null || amt === undefined) return '—';
    return `₦${Number(amt).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

/* ─────────────────────────── Copy to Clipboard ───────────────────────── */
const copyToClipboard = (text) => {
    if (navigator.clipboard) {
        navigator.clipboard.writeText(text).then(() => message.success('Copied!'));
    }
};

/* ──────────────────────────── Main Component ──────────────────────── */
const MerchantTransactions = () => {
    // Handles both:
    // /transactions_history/:id/:clientId (from profile page Txns button)
    // /b2b-products/:productCode/transactions/:merchantId (from B2B Products section)
    const params = useParams();
    const productCode = params.productCode || params.id;
    const merchantId = params.merchantId || params.clientId;
    const location = useLocation();
    const navigate = useNavigate();

    const locationState = location?.state || {};
    const merchantName = locationState.merchantName || merchantId;
    const productName = locationState.productName || productCode;

    /* ── State ── */
    const [transactions, setTransactions] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
    const [selectedTxn, setSelectedTxn] = useState(null);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [txnDetailLoading, setTxnDetailLoading] = useState(false);

    /* Filters */
    const [dateRange, setDateRange] = useState(null);
    const [referenceSearch, setReferenceSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState(null);
    const [sortBy, setSortBy] = useState('transactionDate');
    const [sortDir, setSortDir] = useState('DESC');

    /* ── Fetch transactions using /api/v1/transactions/filter ── */
    const fetchTransactions = useCallback(async (page = 0, size = 20) => {
        if (!merchantId) return;
        setLoading(true);
        try {
            const params = new URLSearchParams({
                merchantId,
                page,
                size,
                sortBy,
                sortDir,
            });

            if (productCode && productCode !== 'all') params.append('productCode', productCode);
            if (dateRange && dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DDTHH:mm:ss'));
            if (dateRange && dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DDTHH:mm:ss'));

            const res = await httpGet(
                `${customBaseUrl.integUrl}/api/v1/transactions/filter?${params.toString()}`
            );

            if (res?.status) {
                const data = res.data;
                // Handle both paginated and array response formats
                const items = Array.isArray(data) ? data : (data?.content || data?.transactions || data?.data || []);
                const total = data?.totalElements || data?.total || items.length;
                setTransactions(items);
                setPagination(prev => ({ ...prev, total }));
            } else {
                message.error(res?.message || 'Failed to fetch transactions');
                setTransactions([]);
            }
        } catch (e) {
            console.error('Fetch transactions error:', e);
            message.error('An error occurred while fetching transactions');
            setTransactions([]);
        } finally {
            setLoading(false);
        }
    }, [merchantId, productCode, dateRange, sortBy, sortDir]);

    /* ── Fetch statistics using /api/v1/transactions/stats ── */
    const fetchStats = useCallback(async () => {
        if (!merchantId) return;
        setStatsLoading(true);
        try {
            const params = new URLSearchParams({ merchantId });
            if (productCode && productCode !== 'all') params.append('productCode', productCode);
            if (dateRange && dateRange[0]) params.append('startDate', dateRange[0].format('YYYY-MM-DDTHH:mm:ss'));
            if (dateRange && dateRange[1]) params.append('endDate', dateRange[1].format('YYYY-MM-DDTHH:mm:ss'));

            const res = await httpGet(
                `${customBaseUrl.integUrl}/api/v1/transactions/stats?${params.toString()}`
            );
            if (res?.status) {
                setStats(res.data);
            }
        } catch (e) {
            console.error('Fetch stats error:', e);
        } finally {
            setStatsLoading(false);
        }
    }, [merchantId, productCode, dateRange]);

    /* ── Fetch transaction by reference ── */
    const fetchTransactionByRef = async (reference) => {
        if (!reference) return;
        setTxnDetailLoading(true);
        try {
            const res = await httpGet(
                `${customBaseUrl.integUrl}/api/v1/transactions/reference/${encodeURIComponent(reference)}`
            );
            if (res?.status) {
                setSelectedTxn(res.data);
                setDrawerOpen(true);
            } else {
                message.error(res?.message || 'Transaction not found');
            }
        } catch (e) {
            console.error('Fetch txn by ref error:', e);
            message.error('Failed to fetch transaction details');
        } finally {
            setTxnDetailLoading(false);
        }
    };

    /* ── Fetch on mount and filter change ── */
    useEffect(() => {
        fetchTransactions(pagination.current - 1, pagination.pageSize);
        fetchStats();
    }, [merchantId, productCode, dateRange]);

    const handleTableChange = (pag, filters, sorter) => {
        if (sorter?.field) {
            setSortBy(sorter.field === 'transactionDate' ? 'transactionDate' : sorter.field);
            setSortDir(sorter.order === 'ascend' ? 'ASC' : 'DESC');
        }
        fetchTransactions(pag.current - 1, pag.pageSize);
        setPagination(pag);
    };

    const handleApplyFilters = () => {
        fetchTransactions(0, pagination.pageSize);
        fetchStats();
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    const handleClearFilters = () => {
        setDateRange(null);
        setStatusFilter(null);
        setReferenceSearch('');
        setTimeout(() => {
            fetchTransactions(0, pagination.pageSize);
            fetchStats();
        }, 100);
    };

    /* ── Export handlers ── */
    const handleExportPDF = () => {
        message.loading({ content: 'Generating PDF…', key: 'pdf', duration: 1.5 });
        setTimeout(() => message.success({ content: 'PDF exported!', key: 'pdf' }), 1600);
    };
    const handleExportExcel = () => {
        message.loading({ content: 'Generating Excel…', key: 'excel', duration: 1.5 });
        setTimeout(() => message.success({ content: 'Excel exported!', key: 'excel' }), 1600);
    };
    const handlePrint = () => window.print();
    const handleShare = () => {
        navigator.clipboard?.writeText(window.location.href);
        message.success('Link copied!');
    };

    /* ── Stats derivations ── */
    const totalCount = stats?.totalTransactions ?? transactions.length;
    const totalVolume = stats?.totalAmount ?? transactions.reduce((s, t) => s + (t.amount || 0), 0);
    const successCount = stats?.successCount ?? transactions.filter(t => ['SUCCESS', 'SUCCESSFUL'].includes((t.status || '').toUpperCase())).length;
    const failedCount = stats?.failedCount ?? transactions.filter(t => t.status?.toUpperCase() === 'FAILED').length;
    const pendingCount = stats?.pendingCount ?? transactions.filter(t => t.status?.toUpperCase() === 'PENDING').length;

    /* ── Client-side search by reference ── */
    const filteredData = referenceSearch
        ? transactions.filter(t =>
            (t.reference || t.transactionReference || t.ref || '').toLowerCase().includes(referenceSearch.toLowerCase())
        )
        : transactions;

    /* ── Status filter ── */
    const visibleData = statusFilter
        ? filteredData.filter(t => (t.status || '').toUpperCase() === statusFilter.toUpperCase())
        : filteredData;

    /* ── Table columns ── */
    const columns = [
        {
            title: 'Reference',
            key: 'reference',
            width: 180,
            render: (_, record) => {
                const ref = record.reference || record.transactionReference || record.ref || '—';
                return (
                    <Space>
                        <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#ff6700', fontSize: 12 }}>
                            {ref.length > 18 ? `...${ref.slice(-14)}` : ref}
                        </span>
                        {ref !== '—' && (
                            <Tooltip title="Copy full reference">
                                <CopyOutlined
                                    style={{ color: '#bbb', cursor: 'pointer', fontSize: 11 }}
                                    onClick={() => copyToClipboard(ref)}
                                />
                            </Tooltip>
                        )}
                    </Space>
                );
            },
        },
        {
            title: 'Narration',
            key: 'narration',
            ellipsis: true,
            render: (_, record) => {
                const narration = record.narration || record.description || record.remarks || '—';
                return <span style={{ color: '#595959', fontSize: 13 }}>{narration}</span>;
            },
        },
        {
            title: 'Product',
            key: 'productCode',
            width: 140,
            render: (_, record) => {
                const code = record.productCode || record.product || productCode || '—';
                return <Tag style={{ borderRadius: 6, background: '#fff3eb', color: '#ff6700', border: '1px solid #ffbb96', fontWeight: 500, fontSize: 11 }}>{code}</Tag>;
            },
        },
        {
            title: 'Amount',
            key: 'amount',
            align: 'right',
            width: 140,
            render: (_, record) => (
                <span style={{ fontWeight: 700, fontSize: 14 }}>
                    {formatAmount(record.amount)}
                </span>
            ),
            sorter: true,
        },
        {
            title: 'Fee',
            key: 'fee',
            align: 'right',
            width: 110,
            render: (_, record) => (
                <span style={{ color: '#8c8c8c', fontSize: 12 }}>
                    {formatAmount(record.fee || record.transactionFee || 0)}
                </span>
            ),
        },
        {
            title: 'Channel',
            key: 'channel',
            width: 110,
            render: (_, record) => {
                const ch = record.channel || record.paymentChannel || '—';
                return <Tag style={{ borderRadius: 6, fontSize: 11 }}>{ch}</Tag>;
            },
        },
        {
            title: 'Date',
            key: 'transactionDate',
            width: 150,
            render: (_, record) => {
                const d = record.transactionDate || record.createdAt || record.date;
                return <span style={{ color: '#595959', fontSize: 12 }}>{d ? dayjs(d).format('DD MMM YY, HH:mm') : '—'}</span>;
            },
            sorter: true,
            defaultSortOrder: 'descend',
        },
        {
            title: 'Status',
            key: 'status',
            width: 120,
            render: (_, record) => <StatusTag status={record.status} />,
        },
        {
            title: '',
            key: 'action',
            width: 50,
            render: (_, record) => {
                const ref = record.reference || record.transactionReference || record.ref;
                return ref ? (
                    <Tooltip title="View details">
                        <Button
                            icon={<EyeOutlined />}
                            size="small"
                            type="text"
                            onClick={() => fetchTransactionByRef(ref)}
                        />
                    </Tooltip>
                ) : null;
            },
        },
    ];

    return (
        <div className="pt-wrapper">
            {/* ── Breadcrumb ── */}
            <PageBreadcrumb
                items={[
                    { label: 'Users', path: '/users' },
                    { label: merchantName, path: `/users/profile/${merchantId}/page` },
                    { label: productCode || 'Product' },
                    { label: 'Transactions' },
                ]}
            />

            {/* ── Page Header ── */}
            <div className="pt-header">
                <div className="pt-header-left">
                    <Avatar
                        size={52}
                        icon={<UserOutlined />}
                        style={{ background: 'linear-gradient(135deg, #ff6700, #ff9500)', fontSize: 24, flexShrink: 0 }}
                    />
                    <div>
                        <h2 className="pt-header-title">{merchantName}</h2>
                        <p className="pt-header-subtitle">
                            Transactions &middot; {productName !== 'all' ? productName : 'All Products'}
                        </p>
                    </div>
                </div>
                <Space wrap size={8}>
                    <Tooltip title="Refresh">
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => { fetchTransactions(0, pagination.pageSize); fetchStats(); }}
                            loading={loading}
                            className="pt-export-btn"
                        />
                    </Tooltip>
                    <Tooltip title="Export to PDF">
                        <Button icon={<FilePdfOutlined />} onClick={handleExportPDF} className="pt-export-btn pt-btn-pdf">PDF</Button>
                    </Tooltip>
                    <Tooltip title="Export to Excel">
                        <Button icon={<FileExcelOutlined />} onClick={handleExportExcel} className="pt-export-btn pt-btn-excel">Excel</Button>
                    </Tooltip>
                    <Tooltip title="Print">
                        <Button icon={<PrinterOutlined />} onClick={handlePrint} className="pt-export-btn pt-btn-print">Print</Button>
                    </Tooltip>
                    <Tooltip title="Share link">
                        <Button icon={<ShareAltOutlined />} onClick={handleShare} className="pt-export-btn pt-btn-share">Share</Button>
                    </Tooltip>
                </Space>
            </div>

            {/* ── Merchant ID Info Strip ── */}
            <div className="mt-merchant-card">
                <div className="mt-merchant-grid">
                    <div className="mt-merchant-field">
                        <IdcardOutlined className="mt-merchant-field-icon" />
                        <div>
                            <div className="mt-merchant-field-label">Merchant ID</div>
                            <div className="mt-merchant-field-value" style={{ fontFamily: 'monospace', color: '#ff6700' }}>
                                {merchantId}
                            </div>
                        </div>
                    </div>
                    {locationState.email && (
                        <div className="mt-merchant-field">
                            <MailOutlined className="mt-merchant-field-icon" />
                            <div>
                                <div className="mt-merchant-field-label">Email</div>
                                <div className="mt-merchant-field-value">{locationState.email}</div>
                            </div>
                        </div>
                    )}
                    {locationState.phone && (
                        <div className="mt-merchant-field">
                            <PhoneOutlined className="mt-merchant-field-icon" />
                            <div>
                                <div className="mt-merchant-field-label">Phone</div>
                                <div className="mt-merchant-field-value">{locationState.phone}</div>
                            </div>
                        </div>
                    )}
                    <div className="mt-merchant-field">
                        <SwapOutlined className="mt-merchant-field-icon" />
                        <div>
                            <div className="mt-merchant-field-label">Product</div>
                            <div className="mt-merchant-field-value">{productCode || 'All'}</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Stats Row ── */}
            <Row gutter={[16, 16]} className="pt-stats-row">
                <Col xs={12} sm={8} md={5}>
                    <Spin spinning={statsLoading} size="small">
                        <div className="pt-stat-card">
                            <SwapOutlined className="pt-stat-icon" style={{ color: '#ff6700' }} />
                            <div>
                                <div className="pt-stat-value">{totalCount.toLocaleString()}</div>
                                <div className="pt-stat-label">Total Transactions</div>
                            </div>
                        </div>
                    </Spin>
                </Col>
                <Col xs={12} sm={8} md={5}>
                    <Spin spinning={statsLoading} size="small">
                        <div className="pt-stat-card">
                            <DollarCircleOutlined className="pt-stat-icon" style={{ color: '#0f9b58' }} />
                            <div>
                                <div className="pt-stat-value" style={{ fontSize: 16 }}>{formatAmount(totalVolume)}</div>
                                <div className="pt-stat-label">Total Volume</div>
                            </div>
                        </div>
                    </Spin>
                </Col>
                <Col xs={8} sm={8} md={5}>
                    <div className="pt-stat-card">
                        <CheckCircleOutlined className="pt-stat-icon" style={{ color: '#52c41a' }} />
                        <div>
                            <div className="pt-stat-value" style={{ color: '#52c41a' }}>{successCount.toLocaleString()}</div>
                            <div className="pt-stat-label">Successful</div>
                        </div>
                    </div>
                </Col>
                <Col xs={8} sm={8} md={4}>
                    <div className="pt-stat-card">
                        <CloseCircleOutlined className="pt-stat-icon" style={{ color: '#ff4d4f' }} />
                        <div>
                            <div className="pt-stat-value" style={{ color: '#ff4d4f' }}>{failedCount.toLocaleString()}</div>
                            <div className="pt-stat-label">Failed</div>
                        </div>
                    </div>
                </Col>
                <Col xs={8} sm={8} md={5}>
                    <div className="pt-stat-card">
                        <ClockCircleOutlined className="pt-stat-icon" style={{ color: '#faad14' }} />
                        <div>
                            <div className="pt-stat-value" style={{ color: '#faad14' }}>{pendingCount.toLocaleString()}</div>
                            <div className="pt-stat-label">Pending</div>
                        </div>
                    </div>
                </Col>
            </Row>

            {/* ── Filter Bar ── */}
            <div className="pt-filter-bar" style={{ marginBottom: 16 }}>
                <div className="pt-filter-title">
                    <FilterOutlined style={{ marginRight: 6 }} />
                    Filters
                </div>
                <div className="pt-filters" style={{ flexWrap: 'wrap', gap: 12 }}>
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Reference Search</label>
                        <Input
                            prefix={<SearchOutlined style={{ color: '#bbb' }} />}
                            placeholder="Search reference…"
                            value={referenceSearch}
                            onChange={e => setReferenceSearch(e.target.value)}
                            style={{ width: 200, borderRadius: 8 }}
                            allowClear
                        />
                    </div>
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Date Range</label>
                        <RangePicker
                            value={dateRange}
                            onChange={range => setDateRange(range)}
                            style={{ borderRadius: 8 }}
                            format="DD MMM YYYY"
                            placeholder={['Start date', 'End date']}
                            disabledDate={d => d && d.isAfter(dayjs())}
                        />
                    </div>
                    <div className="pt-filter-item">
                        <label className="pt-filter-label">Status</label>
                        <Select
                            allowClear
                            placeholder="All statuses"
                            value={statusFilter}
                            onChange={val => setStatusFilter(val)}
                            style={{ width: 160, borderRadius: 8 }}
                        >
                            {['SUCCESS', 'PENDING', 'FAILED', 'REVERSED'].map(s => (
                                <Option key={s} value={s}>{s}</Option>
                            ))}
                        </Select>
                    </div>
                    <Space>
                        <Button
                            type="primary"
                            onClick={handleApplyFilters}
                            style={{ background: '#ff6700', borderColor: '#ff6700', borderRadius: 8 }}
                        >
                            Apply Filters
                        </Button>
                        <Button onClick={handleClearFilters} className="pt-clear-btn">
                            Clear
                        </Button>
                    </Space>
                </div>
            </div>

            {/* ── Table ── */}
            <div className="pt-table-card">
                <div className="pt-table-header-row">
                    <span className="pt-table-count">
                        Showing <strong>{visibleData.length}</strong> transactions
                        {merchantName && <> for <strong>{merchantName}</strong></>}
                        {productCode && productCode !== 'all' && <> · Product: <strong>{productCode}</strong></>}
                    </span>
                </div>
                <Table
                    columns={columns}
                    dataSource={visibleData}
                    rowKey={(r) => r.id || r.reference || r.transactionReference || r.ref || Math.random().toString()}
                    scroll={{ x: 980 }}
                    loading={loading}
                    onChange={handleTableChange}
                    pagination={{
                        current: pagination.current,
                        pageSize: pagination.pageSize,
                        total: pagination.total,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                        showTotal: (total, range) => `${range[0]}–${range[1]} of ${total}`,
                    }}
                    rowClassName={(_, idx) => (idx % 2 === 0 ? 'pt-row-even' : 'pt-row-odd')}
                    className="pt-table"
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={loading ? 'Loading…' : 'No transactions found'}
                            />
                        ),
                    }}
                />
            </div>

            {/* ── Transaction Detail Drawer ── */}
            <Drawer
                title={
                    <Space>
                        <EyeOutlined style={{ color: '#ff6700' }} />
                        <span>Transaction Details</span>
                    </Space>
                }
                open={drawerOpen}
                onClose={() => { setDrawerOpen(false); setSelectedTxn(null); }}
                width={520}
                placement="right"
            >
                {txnDetailLoading ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: 40 }}>
                        <Spin size="large" />
                    </div>
                ) : selectedTxn ? (
                    <div>
                        {/* Status Banner */}
                        <div style={{
                            background: selectedTxn.status?.toUpperCase() === 'SUCCESS' || selectedTxn.status?.toUpperCase() === 'SUCCESSFUL'
                                ? '#f6ffed' : selectedTxn.status?.toUpperCase() === 'FAILED' ? '#fff2f0' : '#fffbe6',
                            border: `1px solid ${selectedTxn.status?.toUpperCase() === 'SUCCESS' || selectedTxn.status?.toUpperCase() === 'SUCCESSFUL'
                                ? '#b7eb8f' : selectedTxn.status?.toUpperCase() === 'FAILED' ? '#ffa39e' : '#ffe58f'}`,
                            borderRadius: 12,
                            padding: '16px 20px',
                            marginBottom: 20,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}>
                            <div>
                                <div style={{ fontSize: 22, fontWeight: 700, color: '#1a1a1a' }}>
                                    {formatAmount(selectedTxn.amount)}
                                </div>
                                <div style={{ fontSize: 12, color: '#8c8c8c', marginTop: 4 }}>Transaction Amount</div>
                            </div>
                            <StatusTag status={selectedTxn.status} />
                        </div>

                        <Descriptions column={1} bordered size="small" labelStyle={{ fontWeight: 600, color: '#595959', width: 140 }}>
                            <Descriptions.Item label="Reference">
                                <Space>
                                    <span style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                        {selectedTxn.reference || selectedTxn.transactionReference || '—'}
                                    </span>
                                    <CopyOutlined
                                        style={{ cursor: 'pointer', color: '#ff6700' }}
                                        onClick={() => copyToClipboard(selectedTxn.reference || selectedTxn.transactionReference)}
                                    />
                                </Space>
                            </Descriptions.Item>
                            <Descriptions.Item label="Narration">
                                {selectedTxn.narration || selectedTxn.description || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Product Code">
                                {selectedTxn.productCode || productCode || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Channel">
                                {selectedTxn.channel || selectedTxn.paymentChannel || '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Fee">
                                {formatAmount(selectedTxn.fee || selectedTxn.transactionFee || 0)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Commission">
                                {formatAmount(selectedTxn.commission || selectedTxn.commissionAmount || 0)}
                            </Descriptions.Item>
                            {selectedTxn.senderAccountNumber && (
                                <Descriptions.Item label="Sender Account">
                                    {selectedTxn.senderAccountNumber}
                                </Descriptions.Item>
                            )}
                            {selectedTxn.receiverAccountNumber && (
                                <Descriptions.Item label="Receiver Account">
                                    {selectedTxn.receiverAccountNumber}
                                </Descriptions.Item>
                            )}
                            {selectedTxn.customerName && (
                                <Descriptions.Item label="Customer">
                                    {selectedTxn.customerName}
                                </Descriptions.Item>
                            )}
                            {selectedTxn.customerId && (
                                <Descriptions.Item label="Customer ID">
                                    <span style={{ fontFamily: 'monospace' }}>{selectedTxn.customerId}</span>
                                </Descriptions.Item>
                            )}
                            <Descriptions.Item label="Transaction Date">
                                {selectedTxn.transactionDate
                                    ? dayjs(selectedTxn.transactionDate).format('DD MMM YYYY, HH:mm:ss')
                                    : selectedTxn.createdAt
                                        ? dayjs(selectedTxn.createdAt).format('DD MMM YYYY, HH:mm:ss')
                                        : '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Merchant ID">
                                <span style={{ fontFamily: 'monospace', color: '#ff6700' }}>
                                    {selectedTxn.merchantId || merchantId}
                                </span>
                            </Descriptions.Item>
                            {selectedTxn.responseCode && (
                                <Descriptions.Item label="Response Code">
                                    <Tag>{selectedTxn.responseCode}</Tag>
                                </Descriptions.Item>
                            )}
                            {selectedTxn.responseMessage && (
                                <Descriptions.Item label="Response Message">
                                    {selectedTxn.responseMessage}
                                </Descriptions.Item>
                            )}
                            {selectedTxn.metadata && (
                                <Descriptions.Item label="Metadata">
                                    <pre style={{ fontSize: 11, background: '#f5f5f5', padding: 8, borderRadius: 6, maxHeight: 120, overflow: 'auto' }}>
                                        {typeof selectedTxn.metadata === 'string'
                                            ? selectedTxn.metadata
                                            : JSON.stringify(selectedTxn.metadata, null, 2)}
                                    </pre>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </div>
                ) : (
                    <Empty description="No transaction data available" />
                )}
            </Drawer>
        </div>
    );
};

export default MerchantTransactions;
